import { useAppDispatch, useLogin, useLogout, useTokens } from '@pkg/hooks';
import { type TwoFactorAuthType } from '@pkg/types';

import { apiFactory, Auth } from '@app/client';
import { prefetch2FA, runAllTwoFactorCallbacks } from '@app/helpers';
import { setError } from '@app/store/actions/error';
import { initializeUser } from '@app/store/actions/user';
import { hide2faExpiredLogin } from '@app/store/slices';

export function use2FA() {
    const dispatch = useAppDispatch();
    const { logout } = useLogout();
    const { setTokens } = useTokens();
    const { postAuthenticate } = useLogin();

    async function disable2FA({
        provider,
        disableAll = undefined,
    }: {
        provider: TwoFactorAuthType;
        disableAll?: boolean;
    }) {
        return apiFactory()
            .call(Auth.disable2FA, {
                prefetch: prefetch2FA(dispatch),
                value: { disable_all: Boolean(disableAll), provider },
            })
            .then((r) => {
                if (r.ok) {
                    logout();
                } else {
                    dispatch(setError({ ...r, notify: true }));
                }
            })
            .catch((e) => {
                dispatch(setError({ ...e, notify: true }));
            });
    }

    async function login2FA({
        code,
        refreshToken,
        callback = undefined,
    }: {
        code: string;
        refreshToken: string;
        callback?: (tokens?: string) => void;
    }) {
        return apiFactory()
            .call(Auth.login2FA, { value: { code, refresh_token: refreshToken } })
            .then((r) => {
                if (r.ok) {
                    setTokens(r.value);
                    dispatch(hide2faExpiredLogin());
                    dispatch(initializeUser());
                    runAllTwoFactorCallbacks();

                    callback?.(r.value.access_token);

                    postAuthenticate();
                } else {
                    dispatch(setError({ ...r, notify: true }));
                    callback?.();
                }
                return r;
            })
            .catch((e) => {
                dispatch(setError({ ...e, notify: true }));
                callback?.();
                return e;
            });
    }

    return {
        disable2FA,
        login2FA,
    };
}
